'use client';

import { useId, type MouseEvent } from 'react';
import { TEXT_TAG, TEXT_VARIANT } from '@pixum/text';
import { Checkbox } from '@pixum/web-checkbox';
import { DIALOG_ROLES, RichTextWithDialogs } from 'components';
import { CART_DATA_PRIVACY_STATUS, useCartCheckoutStore } from 'stores/cart';
import { trackLegalDialogOpen } from '../../analytics/checkout';
import type { DataPrivacyCheckboxProps } from '../types';
import styles from './DataPrivacyCheckbox.module.css';

/** DK only: the legal sentence as a checkbox label; ticking it is required before the order can be placed. */
export function DataPrivacyCheckbox({
  sentence,
  dialogs,
  closeLabel,
  dialogFullScreen,
  center = false,
  errorText
}: DataPrivacyCheckboxProps) {
  const dataPrivacyStatus = useCartCheckoutStore(store => store.dataPrivacyStatus);
  const setDataPrivacyAccepted = useCartCheckoutStore(store => store.setDataPrivacyAccepted);
  const isRejected = dataPrivacyStatus === CART_DATA_PRIVACY_STATUS.REJECTED;
  // The library's fallback id is a random UUID per render (hydration mismatch); useId is stable and unique per instance.
  const checkboxId = useId();

  // A click on a dialog link inside the <label> must open the dialog without toggling the checkbox.
  const preventToggleOnDialogLink = (event: MouseEvent<HTMLSpanElement>) => {
    if ((event.target as HTMLElement).closest('button')) {
      event.preventDefault();
    }
  };

  return (
    <div className={center ? `${styles['dataPrivacy']} ${styles['center']}` : styles['dataPrivacy']}>
      <Checkbox
        id={checkboxId}
        className={styles['checkbox']}
        dataTestId="data-privacy-checkbox"
        checked={dataPrivacyStatus === CART_DATA_PRIVACY_STATUS.ACCEPTED}
        onChange={setDataPrivacyAccepted}
        hasError={isRejected}
        info={isRejected && errorText ? <span role="alert">{errorText}</span> : undefined}
        label={
          <span role="presentation" onClick={preventToggleOnDialogLink}>
            <RichTextWithDialogs
              sentence={sentence}
              dialogs={dialogs}
              variant={TEXT_VARIANT.FOOTNOTE}
              tag={TEXT_TAG.SPAN}
              className={styles['sentence']}
              dialogRole={DIALOG_ROLES.ALERT}
              dialogFullScreen={dialogFullScreen}
              closeLabel={closeLabel}
              onDialogOpen={trackLegalDialogOpen}
            />
          </span>
        }
      />
    </div>
  );
}
